/**
 * cadLibrarySpec — `cad_library` capability (DXF/DWG/STEP/IGES indexed library).
 *
 * Jun 13, 2026 SOTA DRY rewrite — same pattern as ``spreadsheetSpec``: the
 * "Aggiungi account" mental model is wrong for a file-library capability
 * because there's no OAuth account, no vendor credential. The user just
 * wants to either drop files in or point at a cloud folder. We surface
 * exactly those two affordances:
 *
 *   - ``upload``       — drag & drop DXF/DWG/STEP/IGES/STL from desktop.
 *                        Uses the same chat-upload pipeline that powers
 *                        ``POST /api/v1/uploads`` (already wires the
 *                        ingestion → ``cad_features_service`` path).
 *   - ``google_drive`` — pick a folder on a connected Drive. Requires
 *                        Google Drive already linked (the dialog gates
 *                        on the connection presence and prompts the
 *                        user to add the umbrella connection first).
 *   - ``onedrive``     — same as above for OneDrive.
 *   - ``filesystem``   — point at a local NAS / mounted folder via the
 *                        Tauri desktop bridge.
 *
 * The legacy single-vendor "Libreria CAD" wizard with a manual
 * ``root_path`` text field is REMOVED — it was misleading for the SaaS
 * web user (no filesystem access) and redundant for the desktop user
 * (the ``filesystem`` vendor already covers that path).
 *
 * Pattern mirrors ``spreadsheetSpec`` 1:1: ``connectNoun: "file"``
 * makes the FE shell render "Carica file CAD" / "Nessun file collegato"
 * everywhere instead of "Aggiungi account" / "Nessun account collegato".
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Box, FolderTree, Layers, Sparkles, Upload } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import {
  FilesystemIcon,
  GoogleDriveIcon,
  OneDriveIcon,
} from "../components/vendorIcons";

export interface CadLibraryConnection extends BaseConnection {
  /** Original filename when ``vendor === "upload"``; folder path when
   *  ``vendor`` is a cloud source. Display-only — the BE owns the
   *  canonical key. */
  readonly root_path?: string;
  readonly file_count?: number;
}

const iconCls = "h-4 w-4";

/** Vendor ids accepted by the CAD library wizard. Exported so
 *  ``$capability.tsx`` + tests can narrow the route-level vendor
 *  selection without depending on the dialog implementation. */
export type CadLibrarySourceMode =
  | "upload"
  | "google_drive"
  | "onedrive"
  | "filesystem";

export const cadLibrarySpec: CapabilityWizardSpec<CadLibraryConnection> = {
  capability: "cad_library",
  title: msg`Libreria CAD`,
  subtitle: msg`DXF, DWG, STEP, IGES indicizzati per ricerca geometrica`,
  icon: <Box className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 60,

  // The capability isn't an OAuth account — it's a collection of CAD
  // files. The shared FE shell (``resolveConnectNoun``) reads these
  // three fields and substitutes the noun throughout the page chrome:
  // "Carica file CAD" instead of "Aggiungi account", "Nessun file
  // collegato" instead of "Nessun account collegato", and so on. The
  // ``spreadsheet`` capability adopted the same fix in Jun 2026.
  connectNoun: "file",
  connectNounPlural: "file",
  connectNounGender: "m",

  vendors: [
    {
      id: "upload",
      label: msg`Carica file`,
      icon: <Upload className={iconCls} aria-hidden />,
      description: msg`DXF, DWG, STEP, IGES, STL dal computer`,
    },
    {
      id: "google_drive",
      label: msg`Da Google Drive`,
      icon: <GoogleDriveIcon className={iconCls} />,
      description: msg`Richiede Google Drive collegato`,
    },
    {
      id: "onedrive",
      label: msg`Da OneDrive`,
      icon: <OneDriveIcon className={iconCls} />,
      description: msg`Richiede OneDrive collegato`,
    },
    {
      id: "filesystem",
      label: msg`Cartella locale / NAS`,
      icon: <FilesystemIcon className={iconCls} />,
      description: msg`Disponibile solo da Ando Desktop`,
      // The Tauri-only gate is enforced by the dialog: on web this
      // vendor card renders with the disabled treatment + a tooltip
      // suggesting the desktop download.
    },
  ],

  steps: [
    {
      // Same shape as the spreadsheet "source" step — the multi-vendor
      // dialog branches on ``state.vendor`` to render either the
      // drag-and-drop drop-zone (upload), the Google folder picker, the
      // OneDrive folder picker, or the desktop filesystem browser. The
      // step definition itself stays presentational + declarative.
      id: "source",
      kind: "select",
      title: msg`Sorgente`,
      description: msg`Scegli da dove portare i file CAD.`,
    },
    {
      // Optional second step — visible only when the vendor is a cloud
      // source AND the BE returned a folder tree to pick from. The
      // dialog hides it for ``upload`` because the drop-zone already
      // commits the file list inline.
      id: "folder",
      kind: "select",
      title: msg`Cartella`,
      description: msg`Seleziona la cartella che contiene i disegni.`,
    },
  ],

  benefits: [
    {
      icon: <Sparkles className={iconCls} aria-hidden />,
      title: msg`Ricerca geometrica`,
      body: msg`"Quali tavole hanno una flangia DN50?" — risposta con thumbnail.`,
    },
    {
      icon: <Layers className={iconCls} aria-hidden />,
      title: msg`Multi-formato`,
      body: msg`DXF, DWG, STEP, IGES, STL — tutti accessibili in un'unica chat.`,
    },
    {
      icon: <FolderTree className={iconCls} aria-hidden />,
      title: msg`Gerarchia preservata`,
      body: msg`Tag automatici dal nome cartella, nessuna curation manuale.`,
    },
    {
      icon: <Box className={iconCls} aria-hidden />,
      title: msg`Preview leggera`,
      body: msg`SVG renderizzato server-side, nessun download lato client.`,
    },
  ],

  api: createCapabilityApi<CadLibraryConnection>({
    capability: "cad_library",
    defaultVendor: "upload",
  }),
  connectionKeyField: "id" as keyof CadLibraryConnection,
  getDisplayName: (c) => c.root_path ?? c.id ?? i18nSingleton._(msg`Libreria`),
  getSecondaryId: (c) =>
    c.file_count != null ? `${c.file_count} ${i18nSingleton._(msg`file`)}` : null,
};
